//Myapplications.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUserApplications,
  getUserApplicationsByUserId,
  getApplicationByNumberAndPhone,
  deleteApplication
} from '../ServiceAPI/application/api';
import { FileText, CheckCircle, Clock, AlertCircle, Eye, ArrowLeft, Trash2 } from 'lucide-react';
import { isTokenUsable } from '../utils/authSession';

const MyApplications = ({ openLoginModal }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, submitted, documents verified, payment pending, payment completed, in processing, completed, rejected
  const [verificationForm, setVerificationForm] = useState({ applicationId: '', phone: '' });
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedApplication, setVerifiedApplication] = useState(null);

  // Fetch applications on component mount
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        const hasUsableToken = isTokenUsable(token);
        let user = null;

        if (storedUser) {
          try {
            user = JSON.parse(storedUser);
          } catch (parseError) {
            console.error('Failed to parse stored user data:', parseError);
          }
        }

        if (!hasUsableToken && !user?.userId) {
          setApplications([]);
          setError(null);
          setLoading(false);
          return;
        }

        let data;

        if (hasUsableToken) {
          try {
            // Primary path: authenticated endpoint
            data = await getUserApplications({ silent: true });
          } catch (primaryError) {
            if (!user?.userId) {
              throw primaryError;
            }

            // Fallback path: direct user-scoped endpoint for stale cached clients/token edge cases
            data = await getUserApplicationsByUserId(user.userId, { skipAuth: true, silent: true });
          }
        } else {
          data = await getUserApplicationsByUserId(user.userId, { skipAuth: true, silent: true });
        }

        // Handle different response formats
        let applicationsArray = [];
        if (Array.isArray(data)) {
          applicationsArray = data;
        } else if (data.applications && Array.isArray(data.applications)) {
          applicationsArray = data.applications;
        } else if (data.data && Array.isArray(data.data)) {
          applicationsArray = data.data;
        }

        setApplications(applicationsArray);
        setError(null);
      } catch (err) {
        console.error('Error fetching applications:', err);
        setError('Failed to load applications. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const normalizePhone = (value) => String(value ?? '').replace(/\D/g, '');

  const normalizeStatus = (value) =>
    String(value || '').toLowerCase().replace(/[_-]+/g, ' ').trim();

  const handleVerificationSubmit = async (event) => {
    event.preventDefault();

    const applicationNumber = verificationForm.applicationId.trim();
    const phone = normalizePhone(verificationForm.phone);

    if (!applicationNumber || !phone) {
      setVerificationError('Please enter both the application number and phone number.');
      setIsVerified(false);
      setVerifiedApplication(null);
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const data = await getApplicationByNumberAndPhone(applicationNumber, phone);
      const matchedApplication = data?.application ?? null;

      if (matchedApplication) {
        setVerifiedApplication(matchedApplication);
        setIsVerified(true);
        setVerificationError('');
      } else {
        setVerifiedApplication(null);
        setIsVerified(false);
        setVerificationError('No application matched the provided application number and phone number.');
      }
    } catch (err) {
      setVerifiedApplication(null);
      setIsVerified(false);
      if (err?.response?.status === 404) {
        setVerificationError('No application matched the provided application number and phone number.');
      } else {
        console.error('Error looking up application:', err);
        setVerificationError('Something went wrong while verifying your application. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const visibleApplications = isVerified && verifiedApplication ? [verifiedApplication] : [];

  // Filter applications based on selected filter
  const filteredApplications = visibleApplications.filter((app) => {
    if (filter === 'all') return true;
    return normalizeStatus(app.Application_status) === filter;
  });

  // Get status badge color and icon
  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || '').toLowerCase().replace(/[_-]+/g, ' ').trim();
    switch (normalizedStatus) {
      case 'completed':
        return {
          bgColor: 'bg-green-100 dark:bg-green-900',
          textColor: 'text-green-800 dark:text-green-200',
          icon: CheckCircle,
          label: 'Completed'
        };
      case 'submitted':
        return {
          bgColor: 'bg-yellow-100 dark:bg-yellow-900',
          textColor: 'text-yellow-800 dark:text-yellow-200',
          icon: Clock,
          label: 'Submitted'
        };
      case 'documents verified':
        return {
          bgColor: 'bg-blue-100 dark:bg-blue-900',
          textColor: 'text-blue-800 dark:text-blue-200',
          icon: CheckCircle,
          label: 'Documents Verified'
        };
      case 'payment pending':
        return {
          bgColor: 'bg-orange-100 dark:bg-orange-900',
          textColor: 'text-orange-800 dark:text-orange-200',
          icon: Clock,
          label: 'Payment Pending'
        };
      case 'payment completed':
        return {
          bgColor: 'bg-indigo-100 dark:bg-indigo-900',
          textColor: 'text-indigo-800 dark:text-indigo-200',
          icon: CheckCircle,
          label: 'Payment Completed'
        };
      case 'payment didnt receive':
        return {
          bgColor: 'bg-red-100 dark:bg-red-900',
          textColor: 'text-red-800 dark:text-red-200',
          icon: AlertCircle,
          label: "Payment Didn't Receive"
        };
      case 'in processing':
        return {
          bgColor: 'bg-purple-100 dark:bg-purple-900',
          textColor: 'text-purple-800 dark:text-purple-200',
          icon: Clock,
          label: 'In Processing'
        };
      case 'rejected':
        return {
          bgColor: 'bg-red-100 dark:bg-red-900',
          textColor: 'text-red-800 dark:text-red-200',
          icon: AlertCircle,
          label: 'Rejected'
        };
      default:
        return {
          bgColor: 'bg-gray-100 dark:bg-gray-700',
          textColor: 'text-gray-800 dark:text-gray-200',
          icon: FileText,
          label: normalizedStatus
            ? normalizedStatus.replace(/\b\w/g, (c) => c.toUpperCase())
            : 'Unknown'
        };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle view application details
  const handleViewDetails = (application) => {
    navigate(`/application-details/${application.Id}`, {
      state: {
        applicationNumber: application.ApplicationNumber,
        phone: normalizePhone(verificationForm.phone)
      }
    });
  };

  // Handle delete application
  const handleDelete = async (applicationId) => {
    if (window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Token will be automatically added by axios interceptor
        await deleteApplication(applicationId);
        
        // Remove from local state
        setApplications(applications.filter(app => app.Id !== applicationId));
        
        // Toast/Alert
        alert('Application deleted successfully');
      } catch (err) {
        console.error('Error deleting application:', err);
        alert('Failed to delete application. Please try again.');
      }
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const hasUsableToken = isTokenUsable(token);
      let user = null;

      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
        } catch (_parseError) {
          user = null;
        }
      }

      let data;
      if (hasUsableToken) {
        try {
          data = await getUserApplications({ silent: true });
        } catch (_primaryError) {
          if (!user?.userId) throw _primaryError;
          data = await getUserApplicationsByUserId(user.userId, { skipAuth: true, silent: true });
        }
      } else if (user?.userId) {
        data = await getUserApplicationsByUserId(user.userId, { skipAuth: true, silent: true });
      } else {
        return;
      }

      let applicationsArray = [];
      if (Array.isArray(data)) {
        applicationsArray = data;
      } else if (Array.isArray(data.applications)) {
        applicationsArray = data.applications;
      } else if (Array.isArray(data.data)) {
        applicationsArray = data.data;
      }

      setApplications(applicationsArray);
    } catch (err) {
      console.error('Error refreshing applications:', err);
    }
  };

  const handlePayFee = (application) => {
    const applicationStatus = normalizeStatus(application?.Application_status);
    const latestPaymentStatus = String(application?.LatestPaymentStatus || '').toUpperCase();

    // Allow retry when admin marked payment as not received / failed.
    const canRetryPayment =
      applicationStatus === 'payment didnt receive' || latestPaymentStatus === 'FAILED';

    if (!canRetryPayment && latestPaymentStatus && ['PENDING', 'SUCCESS', 'REFUNDED'].includes(latestPaymentStatus)) {
      alert(`Payment form submitted - status: ${latestPaymentStatus}`);
      return;
    }
    navigate(`/payment-form?ids=${application.Id}`, {
      state: {
        applications: [application],
        applicationIds: [application.Id]
      }
    });
  };

  useEffect(() => {
    const refreshAfterPayment = () => {
      fetchApplications();
    };

    window.addEventListener('payment-submitted', refreshAfterPayment);
    return () => window.removeEventListener('payment-submitted', refreshAfterPayment);
  }, []);

  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-[calc(100vh-70px)] p-4 md:p-8">
      <div className='max-w-6xl mx-auto'>
        {/* Header */}
        <div className='mb-8'>
          <div className='flex items-center gap-4 mb-4'>
            <button
              onClick={() => navigate('/user-dashboard')}
              className='flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-cyan-600 dark:hover:border-cyan-600 transition-all font-medium'
            >
              <ArrowLeft className='w-4 h-4' />
              Profile
            </button>
            <h1 className='text-2xl md:text-3xl lg:text-4xl font-bold mb-2'>My Applications</h1>
          </div>
        </div>

        {!loading && !error && !isVerified && (
          <div className='mb-8 rounded-2xl border border-cyan-200 bg-white p-6 shadow-sm dark:border-cyan-900 dark:bg-gray-800'>
            <div className='mb-4'>
              <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>Verify your application</h2>
              <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>Enter the application number and phone number to view the matching application.</p>
            </div>
            <form onSubmit={handleVerificationSubmit} className='grid gap-4 md:grid-cols-[1fr_1fr_auto]'>
              <div>
                <label htmlFor='verification-application-id' className='mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300'>Application Number</label>
                <input
                  id='verification-application-id'
                  type='text'
                  value={verificationForm.applicationId}
                  onChange={(event) => setVerificationForm((prev) => ({ ...prev, applicationId: event.target.value }))}
                  className='w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-700 focus:border-cyan-600 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300'
                  placeholder='e.g. APP00000002'
                />
              </div>
              <div>
                <label htmlFor='verification-phone' className='mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300'>Phone Number</label>
                <input
                  id='verification-phone'
                  type='tel'
                  value={verificationForm.phone}
                  onChange={(event) => setVerificationForm((prev) => ({ ...prev, phone: event.target.value }))}
                  className='w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-700 focus:border-cyan-600 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300'
                  placeholder='Enter phone number'
                />
              </div>
              <div className='flex items-end'>
                <button
                  type='submit'
                  disabled={isVerifying}
                  className='w-full rounded-lg bg-cyan-600 px-4 py-2 font-medium text-white transition-colors hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {isVerifying ? 'Verifying...' : 'View Application'}
                </button>
              </div>
            </form>
            {verificationError && (
              <p className='mt-3 text-sm font-medium text-red-600 dark:text-red-400'>{verificationError}</p>
            )}
          </div>
        )}

        {isVerified && (
          <div className='mb-8'>
            <label htmlFor='application-status-filter' className='block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2'>
              Filter by status
            </label>
            <select
              id='application-status-filter'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className='w-full md:w-[360px] px-4 py-2 rounded-lg font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 focus:outline-none focus:border-cyan-600 dark:focus:border-cyan-600'
            >
              {[
                { value: 'all', label: 'All Applications', count: visibleApplications.length },
                { value: 'submitted', label: 'Submitted', count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'submitted').length },
                { value: 'documents verified', label: 'Documents Verified', count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'documents verified').length },
                { value: 'payment pending', label: 'Payment Pending', count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'payment pending').length },
                { value: 'payment didnt receive', label: "Payment Didn't Receive", count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'payment didnt receive').length },
                { value: 'payment completed', label: 'Payment Completed', count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'payment completed').length },
                { value: 'in processing', label: 'In Processing', count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'in processing').length },
                { value: 'completed', label: 'Completed', count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'completed').length },
                { value: 'rejected', label: 'Rejected', count: visibleApplications.filter(a => normalizeStatus(a.Application_status) === 'rejected').length }
              ].map(option => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className='flex items-center justify-center py-12'>
            <div className='text-center'>
              <div className='w-12 h-12 border-4 border-gray-300 dark:border-gray-600 border-t-cyan-600 rounded-full animate-spin mx-auto mb-4'></div>
              <p className='text-gray-600 dark:text-gray-400'>Loading your applications...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className='bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-800 dark:text-red-200 px-4 py-4 rounded-lg mb-6'>
            <p className='font-semibold mb-2'>Error</p>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className='mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors'
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && isVerified && filteredApplications.length === 0 && (
          <div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 text-center'>
            <FileText className='w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4' />
            <h2 className='text-2xl font-semibold mb-2'>No Applications Found</h2>
            <p className='text-gray-600 dark:text-gray-400 mb-6'>
              {filter === 'all'
                ? 'No application matches the provided details yet.'
                : `No applications with status "${filter}".`}
            </p>
            <button
              onClick={() => setIsVerified(false)}
              className='px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium'
            >
              Try Another Search
            </button>
          </div>
        )}

        {/* Applications Grid */}
        {!loading && !error && isVerified && filteredApplications.length > 0 && (
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8'>
            {filteredApplications.map((application) => {
              const statusInfo = getStatusBadge(application.Application_status);
              const StatusIcon = statusInfo.icon;
              const paymentNotReceived = normalizeStatus(application.Application_status) === 'payment didnt receive';
              const showReuploadAlert = application.HasReuploadRequest > 0;
              const showAlertBanner = showReuploadAlert || paymentNotReceived;

              return (
                <div
                  key={application.Id}
                  className={`bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden border ${showAlertBanner
                      ? (paymentNotReceived ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50' : 'border-orange-500 ring-2 ring-orange-500 ring-opacity-50')
                      : 'border-gray-200 dark:border-gray-700'
                    }`}
                >
                  {/* Reupload Request Alert */}
                  {showReuploadAlert && (
                    <div className="bg-orange-500 text-white text-xs font-bold py-1 px-4 text-center animate-pulse">
                      REUPLOAD REQUIRED
                    </div>
                  )}
                  {paymentNotReceived && (
                    <div className="bg-red-500 text-white text-xs font-bold py-1 px-4 text-center animate-pulse">
                      PAYMENT NOT RECEIVED
                    </div>
                  )}

                  {/* Card Header with Status */}
                  <div className='p-4 border-b border-gray-200 dark:border-gray-700'>
                    <div className='flex items-start justify-between mb-4'>
                      <div className='flex-1'>
                        <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-1'>
                          {application.ServiceName}
                        </h3>
                        <p className='text-sm text-gray-600 dark:text-gray-400'>
                          {application.ServiceType || 'Type not specified'} • {application.ServiceCategory || 'Service'}
                        </p>
                      </div>
                      <div className={`${statusInfo.bgColor} ${statusInfo.textColor} px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium`}>
                        <StatusIcon className='w-4 h-4' />
                        {statusInfo.label}
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className='p-4 space-y-4'>
                    {/* Created Date */}
                    <div className='flex items-start gap-3'>
                      <div>
                        <label className='text-md font-semibold text-gray-500 dark:text-gray-400 tracking-wide'>Submitted On: </label>
                        <span className='text-md font-semibold text-red-500'>{formatDate(application.Created_at)}</span>
                      </div>
                    </div>

                    {/* Application ID */}
                    <div>
                      <label className='text-md font-semibold text-gray-500 dark:text-gray-400 tracking-wide'>Application ID: </label>
                      <span className='text-md font-semibold text-red-500'>{application.Id}</span>
                    </div>

                    {/* Remarks (if available) */}
                    {application.Remarks && (
                      <div>
                        <label className='text-md font-semibold text-gray-500 dark:text-gray-400 tracking-wide'>Remarks By Admin</label>
                        <p className='text-gray-700 dark:text-gray-300 mt-1 text-sm bg-gray-100 dark:bg-gray-700 rounded p-3'>
                          {application.Remarks}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Card Footer - Actions */}
                  <div className='p-6 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-3'>
                    <button
                      onClick={() => handleViewDetails(application)}
                      className='flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium text-sm'
                    >
                      <Eye className='w-4 h-4' />
                      View Details
                    </button>
                    <button
                      onClick={() => handlePayFee(application)}
                      disabled={!['documents verified', 'payment pending', 'payment didnt receive'].includes(normalizeStatus(application.Application_status))}
                      className='flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-cyan-600'
                    >
                      <Eye className='w-4 h-4' />
                      Pay Fee
                    </button>
                    <button
                      onClick={() => handleDelete(application.Id)}
                      className='flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors font-medium text-sm'
                      title="Delete Application"
                    >
                      <Trash2 className='w-4 h-4' />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplications;